import { ProductItem } from "@exopengithub/exokit";
import { skipToken } from "@reduxjs/toolkit/query";
import {
	Fragment,
	type MouseEventHandler,
	type ReactElement,
	type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { useGetConversationsQuery } from "src/features/ai-chat/api/aiChatApi.js";
import { useOnboardingStatus } from "src/features/ai-chat/hooks/useOnboardingStatus.js";
import { useExopenAiAccess } from "src/features/ai-chat/hooks/useHasExopenAiAccess.js";
import {
	getProduct,
	SALES_CONTACT_URL,
} from "src/features/products/products.js";
import { useProductAccess } from "src/features/products/useProductAccess.js";
import { useGetAllIntegrationsQuery } from "src/common/service/fusionHub/fusionHubApi.js";
import { useEwrLinkClick } from "src/common/service/useEwrLinkClick.js";
import { useExcelReportFiles } from "src/features/excelReports/hooks/useExcelReportFiles.js";
import { useGetPlansForCompanyDomainQuery } from "src/features/planning/api/planApi.js";
import { useGetCompanyModulesQuery } from "src/features/planning/api/planningApi.js";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks.js";
import { useGetIntegrationsQuery } from "src/common/service/nexus/nexusApi.js";
import {
	useGetDestinationTimestampsQuery,
	useGetReportStatusQuery,
} from "src/common/redux/api/exopenApi.js";
import {
	mergeDataSources,
	scopedToLegalEntity,
} from "src/features/integrations/overview/table/mergeDataSources.js";
import { useLoadedLegalEntities } from "src/routes/routeDataHooks.js";
import { plansInScope } from "src/features/planning/plans/planUtils.ts";
import { useGetPowerBiReportsQuery } from "src/features/powerBiReports/api/powerBiReportsApi.js";
import { toSetupStatus } from "src/features/powerBiReports/setup/powerBiSetupStatus.js";
import { ExopenProductsWidgetView } from "./ExopenProductsWidgetView.js";

interface ExopenProductRow {
	name: string;
	description: ReactNode;
	active: boolean;
	/** One step short of active — company opted in, but this user's own onboarding consent is still pending. */
	pending?: boolean | undefined;
	/** Empty for inactive products — the row renders without a link. */
	path: string;
	onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
	/** Inactive only: where "Aktivera" goes — a self-service flow, else the sales contact page. */
	activateHref?: string | undefined;
	/** Inactive with no way to activate from here — plain description, no activate link. */
	unavailable?: boolean | undefined;
	/** Access not answered yet — neither active nor offered for activation. */
	loading?: boolean | undefined;
}

// WHY no Figma upsell rows: they need the entitlement API (portal#4281)
export const ExopenProductsWidget = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const legalEntities = useLoadedLegalEntities();
	const { legalEntityId } = useParams();
	const {
		access,
		excelTarget,
		loading: productLoading,
		failed: productFailed,
	} = useProductAccess();
	const { onClick: handleEwrClick } = useEwrLinkClick(
		excelTarget?.kind === "ewr" ? excelTarget.url : "",
	);
	const isReportPicker = excelTarget?.kind === "reportPicker";
	// Same documents/me query as the Rapporter widget, so the count matches its tree.
	const {
		files: reportFiles,
		isLoading: isLoadingReportFiles,
		isError: isReportFilesError,
	} = useExcelReportFiles(isReportPicker ? companyDomain.id : skipToken);
	// Same reports query as the Power BI card, so the count matches its list; only once connected, since it queries the customer's Power BI.
	const { currentData: powerBiReports, error: powerBiReportsError } =
		useGetPowerBiReportsQuery(
			access.powerBi.active ? companyDomain.id : skipToken,
		);
	const powerBiSetup = toSetupStatus({
		reports: powerBiReports,
		isLoading: !powerBiReports && !powerBiReportsError,
		error: powerBiReportsError,
	});

	const { data: fusionHubIntegrations, isError: isFusionHubError } =
		useGetAllIntegrationsQuery({ companyDomainId: companyDomain.id });
	const { data: nexusIntegrations, isError: isNexusError } =
		useGetIntegrationsQuery({ companyDomainId: companyDomain.id });
	const { data: destinationTimestamps } = useGetDestinationTimestampsQuery({
		companyDomainId: companyDomain.id,
	});
	const { data: plans, isError: isPlansError } =
		useGetPlansForCompanyDomainQuery({
			companyDomainId: companyDomain.id,
		});
	// Same query useProductAccess already fires for this domain — RTK Query dedupes into one request.
	const { data: planningModules } = useGetCompanyModulesQuery({
		companyId: companyDomain.id,
	});
	const { data: reportStatus } = useGetReportStatusQuery({
		companyDomainId: companyDomain.id,
	});
	// Company opt-in and this user's own onboarding consent are separate gates — see AiChatPage.
	const { needsOnboarding, isLoading: isOnboardingLoading } =
		useOnboardingStatus(companyDomain.id);
	const hasAiChat = access.ai.active;
	const aiAccess = useExopenAiAccess(companyDomain.id);
	const aiUnavailableText =
		!aiAccess.allowed && aiAccess.reason === "needs-domain-wide-access"
			? t("Requires access to every company in the domain")
			: t("Not available");
	const { data: aiConversations, isError: isAiConversationsError } =
		useGetConversationsQuery(
			hasAiChat && !needsOnboarding ? companyDomain.id : skipToken,
		);

	const mergedDataSources =
		fusionHubIntegrations && nexusIntegrations
			? mergeDataSources({
					legalEntities,
					fusionHubIntegrations: fusionHubIntegrations.items,
					nexusIntegrations,
					destinationTimestamps: destinationTimestamps ?? [],
					// A count only; no sync status is rendered, so no jobs query.
					activeJobs: [],
				})
			: undefined;
	const dataSourceCount =
		mergedDataSources === undefined
			? undefined
			: scopedToLegalEntity(mergedDataSources, legalEntityId).length;
	// WHY no timestamps: a count does not render sync times.
	const dataSourceCountFailed = isFusionHubError || isNexusError;
	const reportModuleCount = reportStatus?.reportModules.length ?? 0;
	const cloudPlatform = getProduct("cloudPlatform");
	const intelligenceLayer = getProduct("intelligenceLayer");
	const planning = getProduct("planning");
	const ai = getProduct("ai");
	const excel = getProduct("excel");
	const powerBi = getProduct("powerBi");

	// WHY: the rows only add per-product copy — which products exist and who has them is decided in src/features/products.
	const products: ExopenProductRow[] = [
		{
			name: cloudPlatform.name,
			description: dataSourceCountFailed
				? t("Could not load count")
				: dataSourceCount === undefined
					? t("Loading")
					: t("{{count}} data source", { count: dataSourceCount }),
			active: access.cloudPlatform.active,
			path: cloudPlatform.path(companyDomain, { legalEntityId }),
		},
		...(access.intelligenceLayer.hidden
			? []
			: [
					{
						name: intelligenceLayer.name,
						description: access.intelligenceLayer.active
							? t("Data model and raw data")
							: t("Not activated"),
						active: access.intelligenceLayer.active,
						path: access.intelligenceLayer.active
							? intelligenceLayer.path(companyDomain)
							: "",
					},
				]),
		{
			name: planning.name,
			description: productLoading.planning ? (
				t("Loading")
			) : !access.planning.active ? (
				t("Not activated")
			) : isPlansError ? (
				t("Could not load count")
			) : plans ? (
				<>
					<Link
						to={planning.path(companyDomain, { legalEntityId })}
						className="truncate hover:underline"
					>
						{t("{{count}} plan", {
							count: plansInScope(plans, legalEntityId).length,
						})}
					</Link>
					<Link
						to={`/company-domains/${companyDomain.id}/settings/planning`}
						className="truncate hover:underline"
					>
						{t("{{count}} module", {
							count: planningModules?.length ?? 0,
						})}
					</Link>
				</>
			) : (
				t("Efficient and user-friendly budget and forecast")
			),
			active: access.planning.active,
			loading: productLoading.planning,
			// WHY no path: the description above holds its own links (plans, modules) — an outer Link here would nest anchors.
			path: "",
			activateHref: planning.activatePath?.(companyDomain),
		},
		{
			name: ai.name,
			// WHY stages: not available -> activate (company opt-in) -> approve terms (this user's consent) -> in use, same as the AI chat widget.
			description: access.ai.unavailable
				? aiUnavailableText
				: productLoading.ai || isOnboardingLoading
					? t("Loading")
					: !hasAiChat
						? t("Activate")
						: needsOnboarding
							? t("Approve the terms to start chatting")
							: isAiConversationsError
								? t("Could not load count")
								: t("{{count}} conversation", {
										count: aiConversations?.length ?? 0,
									}),
			active: hasAiChat,
			pending: hasAiChat && needsOnboarding,
			unavailable: access.ai.unavailable,
			path: hasAiChat ? ai.path(companyDomain) : "",
			activateHref: ai.activatePath?.(companyDomain),
		},
		// The picker row has no loading/unknown state: v7 access is known synchronously.
		isReportPicker
			? {
					name: excel.name,
					description: isLoadingReportFiles
						? t("Loading")
						: isReportFilesError
							? t("Could not load count")
							: t("{{count}} report", { count: reportFiles.length }),
					active: access.excel.active,
					path: excelTarget.path,
				}
			: {
					name: excel.name,
					description: productLoading.excel
						? t("Loading")
						: productFailed.excel
							? t("Could not load status")
							: access.excel.active
								? t("{{count}} report module", { count: reportModuleCount })
								: t("Not activated"),
					active: access.excel.active,
					loading: productLoading.excel,
					// A failed status means entitlement is unknown, not absent — don't send an existing customer to sales.
					unavailable: productFailed.excel,
					path: excelTarget?.url ?? "",
					onClick: excelTarget ? handleEwrClick : undefined,
				},
		...(access.powerBi.hidden
			? []
			: [
					{
						name: powerBi.name,
						description: productLoading.powerBi
							? t("Loading")
							: productFailed.powerBi
								? t("Could not load status")
								: !access.powerBi.active
									? t("Not activated")
									: powerBiSetup.kind === "ready"
										? t("{{count}} report", {
												count: powerBiSetup.reports.length,
											})
										: powerBiSetup.kind === "loading"
											? t("Loading")
											: powerBiSetup.kind === "setupIncomplete"
												? t("Finish the setup")
												: t("Could not load count"),
						active: access.powerBi.active,
						loading: productLoading.powerBi,
						// A failed status means the connection is unknown, not absent — don't send an existing customer to sales.
						unavailable: productFailed.powerBi,
						path: access.powerBi.active ? powerBi.path(companyDomain) : "",
						activateHref: powerBi.activatePath?.(companyDomain),
					},
				]),
	];

	// The container owns routing/marketing links so the View stays portable.
	const items: ReactElement[] = products.map((product) => {
		const canActivate =
			!product.active && !product.unavailable && !product.loading;
		const activateHref = product.activateHref ?? SALES_CONTACT_URL;
		const content = (
			<ProductItem
				title={product.name}
				// WHY t("Activate") when activatable: activateLinkProps turns this
				// into the "Aktivera →" link itself — a separate action link
				// alongside it would just repeat the same "go activate" prompt.
				description={canActivate ? t("Activate") : product.description}
				active={product.active}
				pending={product.pending}
				// The status icon is a second, less-discoverable way to reach
				// the same activation link the description above already is.
				activateLinkProps={
					canActivate
						? {
								href: activateHref,
								...(activateHref.startsWith("http") && {
									target: "_blank",
									rel: "noopener noreferrer",
								}),
								"aria-label": `${t("Activate")} ${product.name}`,
							}
						: undefined
				}
			/>
		);
		if (!(product.active && product.path)) {
			return <Fragment key={product.name}>{content}</Fragment>;
		}
		return (
			<Link
				key={product.name}
				to={product.path}
				viewTransition
				onClick={product.onClick}
				className="block"
				{...(product.path.startsWith("http") && {
					target: "_blank",
					rel: "noopener noreferrer",
				})}
			>
				{content}
			</Link>
		);
	});

	return <ExopenProductsWidgetView items={items} />;
};
